import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { brokerOutreachApi, type BrokerOutreachCardDto } from "@/api/brokerOutreach";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { cn } from "@/design-system/lib/utils";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { HeaderAction } from "../introCards/HeaderAction";
import { IntroCardPill } from "../introCards/IntroCardPill";
import { draftNewerThanSend, isDone, latestVersion, outreachCardStatus } from "../introCards/introCardStatus";
import { EditAndSendAgain, SentFold } from "../introCards/SentFold";
import { writeClipboard } from "../outreach/outreachCopy";
import { brokerOutreachCopy, wordCount } from "./brokerOutreachCopy";
import { ListingLinkLine } from "./ListingLinkLine";
import { CardSentLine } from "./CardStatusLine";
import { useInvalidateWithOutreachState } from "./useBrokerOutreach";

/**
 * The follow-up email (D33) — drafted only on request, and only once the broker
 * intro email has gone (the card says why otherwise).
 *
 * <p>The email card's two send paths, unchanged in shape (R4): SEND FROM
 * PHOENIX, or COPY then RECORD SEND. Copying writes nothing; RECORD SEND records
 * exactly the subject and text COPY placed on the clipboard.</p>
 *
 * <p>🔴 <b>The email card's locked trailers.</b> The sender's sign-off and
 * Phoenix's role statement are appended by the transport and are already part of
 * what COPY takes — no mail client appends the role statement, so a copy without
 * it would be the one path by which this reaches a broker undisclosed. Both are
 * shown as text, never as fields.</p>
 *
 * <p>Both sends use the SAVED draft — the copy text is composed server-side from
 * it — so both are disabled while there are unsaved edits.</p>
 *
 * <p><b>A collapsible card (D2).</b> The typed text, the copied flag and the
 * send confirmation live here, above the hidden body, so a collapse loses
 * nothing and the collapsed header's SEND FROM PHOENIX (J5) is disabled by the
 * same unsaved edit and opens the same confirmation as the body's.</p>
 */
/** The sentence an unsaved edit shows — under the composer, and as the collapsed
 *  header's SEND tooltip (J5). */
const UNSAVED = "Save your changes before sending or copying — both use the saved draft.";

export function BrokerFollowUpEmailCard({
  submissionId,
  card,
  agentEmail,
  maxWords,
  canWrite,
  open,
  onOpenChange,
  onActed,
}: {
  submissionId: string;
  card: BrokerOutreachCardDto;
  agentEmail: string | null;
  maxWords: number;
  canWrite: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Called after a send or RECORD SEND this tab performed succeeds (J6). */
  onActed?: () => void;
}) {
  const toast = useToast();
  const draft = card.draft;
  const [copied, setCopied] = useState<{ subject: string; body: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const invalidateWithState = useInvalidateWithOutreachState(submissionId);
  const history = useSendHistory(submissionId);

  // The typed text, reseeded whenever a (re)draft lands.
  const saved = draft?.body ?? "";
  const [seededFrom, setSeededFrom] = useState(draft?.generatedAtUtc ?? null);
  const [text, setText] = useState(saved);
  if ((draft?.generatedAtUtc ?? null) !== seededFrom) {
    setSeededFrom(draft?.generatedAtUtc ?? null);
    setText(saved);
  }
  const dirty = text.trim() !== saved.trim();
  const empty = text.trim().length === 0;

  const generate = useMutation({
    mutationFn: () => brokerOutreachApi.draft(submissionId, "follow-up-email"),
    onSuccess: (s) => {
      invalidateWithState(s);
      setCopied(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const record = useMutation({
    mutationFn: (c: { subject: string; body: string }) => brokerOutreachApi.recordFollowUpEmail(submissionId, c),
    onSuccess: (s) => {
      invalidateWithState(s, { logged: true });
      setCopied(null);
      setRevealed(false);
      toast.success("Recorded as sent from your own email.");
      onActed?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: () => brokerOutreachApi.sendFollowUpEmail(submissionId),
    onSuccess: (s) => {
      invalidateWithState(s, { logged: true });
      setRevealed(false);
      toast.success(agentEmail ? `Sent to ${agentEmail}.` : "Sent.");
      onActed?.();
    },
    // Every refusal happens before the transport call — the server's sentence
    // names the next action (including "take the copy").
    onError: (e: Error) => toast.error(e.message),
  });

  const status = outreachCardStatus(card, copied != null);
  const done = isDone(status);
  const latest = latestVersion(history.data?.brokerFollowUpEmails);
  const unsentDraft = draft != null && draftNewerThanSend([draft.generatedAtUtc, draft.editedAtUtc], card.sentAtUtc);
  // J8 — writers only: a reader keeps today's read-only view, with no control to reach.
  // The editor area hides by CLASS as well as the attribute: Tailwind's preflight
  // `[hidden]` rule loses to `.grid` (same specificity, emitted earlier).
  const editorHidden = canWrite && done && !revealed && !unsentDraft;

  const action = !canWrite ? null : status.state === "notDrafted" ? (
    <HeaderAction
      label={generate.isPending ? "Drafting…" : "Draft follow-up"}
      onClick={() => generate.mutate()}
      disabled={!card.available || generate.isPending}
      reason={card.unavailableReason}
      testId="broker-followup-email-header-draft"
    />
  ) : status.state === "copied" ? (
    <HeaderAction
      label={record.isPending ? "Recording…" : "Record send"}
      onClick={() => copied && record.mutate(copied)}
      disabled={record.isPending}
      testId="broker-followup-email-header-record"
    />
  ) : status.state === "draftSaved" ? (
    <HeaderAction
      label={send.isPending ? "Sending…" : "Send from Phoenix"}
      onClick={() => setConfirming(true)}
      disabled={dirty || empty || send.isPending || !card.available}
      reason={dirty ? UNSAVED : card.unavailableReason}
      testId="broker-followup-email-header-send"
    />
  ) : null;

  return (
    <CollapsibleCard
      open={open}
      onOpenChange={onOpenChange}
      title="Follow-up email"
      recipient="To the listing broker"
      pill={<IntroCardPill status={status} />}
      action={action}
      testId="broker-followup-email-card"
    >
      <div className="grid gap-space-3">
        {done ? <CardSentLine card={card} testId="broker-followup-email-sent-line" /> : null}
        {done && latest ? <SentFold body={latest.body} testId="broker-followup-email-sent" /> : null}

        {!card.available ? (
          <p className="text-small text-status-warning m-0" data-testid="broker-followup-email-unavailable">
            {card.unavailableReason}
          </p>
        ) : null}

        {editorHidden && canWrite ? (
          <EditAndSendAgain onClick={() => setRevealed(true)} testId="broker-followup-email-edit-again" />
        ) : null}

        <div hidden={editorHidden} className={cn("grid gap-space-3", editorHidden && "hidden")} data-testid="broker-followup-email-editor">
          {!draft ? (
            canWrite ? (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => generate.mutate()}
                  disabled={!card.available || generate.isPending}
                  data-testid="broker-followup-email-draft"
                >
                  {generate.isPending ? "Drafting…" : "Draft follow-up"}
                </Button>
              </div>
            ) : (
              <p className="text-small text-gray-mid m-0">Nothing drafted yet.</p>
            )
          ) : (
            <FollowUpComposer
              submissionId={submissionId}
              card={card}
              text={text}
              onTextChange={setText}
              dirty={dirty}
              empty={empty}
              maxWords={maxWords}
              canWrite={canWrite}
              copied={copied}
              onCopied={setCopied}
              onRedraft={() => generate.mutate()}
              redrafting={generate.isPending}
              onRecord={(c) => record.mutate(c)}
              recording={record.isPending}
              onSend={() => setConfirming(true)}
              sending={send.isPending}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirming}
        title="Send the follow-up from Phoenix"
        message={
          <>
            This goes to the listing broker{agentEmail ? ` at ${agentEmail}` : ""} under your name, with your
            sign-off and Phoenix&apos;s role statement. It does not mark the buyer as contacted.
          </>
        }
        confirmLabel="Send from Phoenix"
        onConfirm={() => {
          setConfirming(false);
          send.mutate();
        }}
        onCancel={() => setConfirming(false)}
      />
    </CollapsibleCard>
  );
}

function FollowUpComposer({
  submissionId,
  card,
  text,
  onTextChange,
  dirty,
  empty,
  maxWords,
  canWrite,
  copied,
  onCopied,
  onRedraft,
  redrafting,
  onRecord,
  recording,
  onSend,
  sending,
}: {
  submissionId: string;
  card: BrokerOutreachCardDto;
  /** The typed text — held by the card so a collapse keeps it (D2). */
  text: string;
  onTextChange: (next: string) => void;
  dirty: boolean;
  empty: boolean;
  maxWords: number;
  canWrite: boolean;
  copied: { subject: string; body: string } | null;
  onCopied: (c: { subject: string; body: string }) => void;
  onRedraft: () => void;
  redrafting: boolean;
  onRecord: (c: { subject: string; body: string }) => void;
  recording: boolean;
  /** Opens the card's send confirmation. */
  onSend: () => void;
  sending: boolean;
}) {
  const toast = useToast();
  const draft = card.draft!;
  const invalidateWithState = useInvalidateWithOutreachState(submissionId);
  const words = wordCount(text);
  const subject = draft.subject ?? "";

  const save = useMutation({
    mutationFn: () => brokerOutreachApi.saveDraft(submissionId, "follow-up-email", text),
    onSuccess: (s) => {
      invalidateWithState(s);
      toast.success("Draft saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copy = async () => {
    const body = draft.copyText ?? draft.body;
    if (await writeClipboard(body)) {
      onCopied({ subject, body });
      toast.success("Message copied — the subject is above it.");
    } else {
      toast.error("Couldn't copy the message. Select it and copy by hand.");
    }
  };

  return (
    <div className="grid gap-space-3">
      <div className="grid gap-space-1">
        <div className="flex items-center justify-between gap-space-2">
          <span className="text-small font-medium">Subject</span>
          <Button
            size="sm"
            variant="text"
            onClick={() =>
              void writeClipboard(subject).then((ok) =>
                ok ? toast.success("Subject copied.") : toast.error("Couldn't copy the subject."),
              )
            }
          >
            Copy subject
          </Button>
        </div>
        <p
          className="text-small text-onyx m-0 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
          data-testid="broker-followup-email-subject"
        >
          {subject}
        </p>
      </div>

      <Textarea
        rows={8}
        value={text}
        readOnly={!canWrite}
        aria-label="Follow-up email message"
        data-testid="broker-followup-email-body"
        onChange={(e) => onTextChange(e.target.value)}
      />
      <span
        className={`text-caption ${words > maxWords ? "text-status-warning-ink" : "text-gray-mid"}`}
        data-testid="broker-followup-email-words"
      >
        {words} / {maxWords} words{words > maxWords ? ` — ${brokerOutreachCopy.overWords(maxWords)}` : ""}
      </span>

      <ListingLinkLine line={draft.listingLinkLine ?? null} testId="broker-followup-email-listing-link" />

      <div className="grid gap-space-2" data-testid="broker-followup-email-trailers">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          Added automatically, and not editable
        </span>
        <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2 grid gap-space-2">
          {draft.signatureBlock ? (
            <p className="text-small text-gray-mid whitespace-pre-wrap m-0">{draft.signatureBlock}</p>
          ) : (
            <p className="text-small text-gray-mid m-0">
              You have no signature on file, so the message goes out without one.
            </p>
          )}
          <p className="text-small text-gray-mid whitespace-pre-wrap m-0" data-testid="broker-followup-email-role">
            {draft.roleStatement}
          </p>
        </div>
      </div>

      {canWrite ? (
        <>
          {dirty ? (
            <p role="status" className="text-small text-status-warning m-0">
              {UNSAVED}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-space-3">
            <Button
              onClick={onSend}
              disabled={dirty || empty || sending || !card.available}
              data-testid="broker-followup-email-send"
            >
              {sending ? "Sending…" : "Send from Phoenix"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void copy()}
              disabled={dirty || empty || !card.available}
              data-testid="broker-followup-email-copy"
            >
              Copy for my own email
            </Button>
            <Button
              variant="secondary"
              onClick={() => copied && onRecord(copied)}
              disabled={!copied || recording}
              data-testid="broker-followup-email-record"
            >
              {recording ? "Recording…" : "Record send"}
            </Button>
            <Button variant="text" onClick={() => save.mutate()} disabled={!dirty || empty || save.isPending}>
              {save.isPending ? "Saving…" : "Save draft"}
            </Button>
            <Button variant="text" onClick={onRedraft} disabled={redrafting}>
              {redrafting ? "Redrafting…" : "Redraft"}
            </Button>
          </div>
          {copied ? (
            <p role="status" className="text-small text-gray-mid m-0" data-testid="broker-followup-email-copied">
              {brokerOutreachCopy.copiedRecordSend}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-caption text-gray-mid m-0">Only this buyer&apos;s consultant can send it.</p>
      )}
    </div>
  );
}
